import type { ColumnFiltersState } from '@tanstack/react-table'
import { apiClient } from '@/shared/lib/api/api-client'
import type { SortModel, PaginationModel, FilterValue } from '@/shared/lib/api/api-types'
import type {
  BankStatement,
  BankStatementAnalysis,
  BankStatementFilter,
  BankStatementFilterValues,
  BankStatementParser,
  BankStatementUploadPayload,
  ManualUploadAccount,
} from '../types/bank-statement'

export interface BankStatementListResponse {
  Records: BankStatement[]
  TotalRecords: number
  TotalActives: number
  PageNumber: number
  PageSize: number
  TotalPages: number
}

export interface BankStatementListRequest {
  filter: BankStatementFilter
  sorting: SortModel[]
  pagination: PaginationModel
}

/**
 * Maps TanStack column filters + filter values → BankStatementFilter for the backend.
 * Set filters on processStatus send IDs (looked up via filterValues).
 */
export function buildBankStatementFilter(
  columnFilters: ColumnFiltersState,
  filterValues: BankStatementFilterValues | undefined,
): BankStatementFilter {
  const filter: BankStatementFilter = {}

  for (const cf of columnFilters) {
    const v = cf.value as FilterValue | undefined
    if (!v) continue

    if (v.filterType === 'text' && v.filter) {
      if (cf.id === 'origin') filter.origin = v.filter
      else if (cf.id === 'institutionName') filter.institutionName = v.filter
      else if (cf.id === 'accountNumber') filter.accountNumber = v.filter
      else if (cf.id === 'accountName') filter.accountName = v.filter
      else if (cf.id === 'companyName') filter.companyName = v.filter
      else if (cf.id === 'fileName') filter.fileName = v.filter
      else if (cf.id === 'currencyIsoCode') filter.currencyIsoCode = v.filter
      else if (cf.id === 'statementNumber') filter.statementNumber = v.filter
    } else if (v.filterType === 'set' && v.values.length > 0) {
      if (cf.id === 'processStatus') {
        filter.processStatus = v.values
          .map((l) => filterValues?.status.find((o) => o.Label === l)?.Id as number)
          .filter(Boolean)
      }
    }
  }

  return filter
}

export function fetchBankStatements(req: BankStatementListRequest) {
  return apiClient.post<BankStatementListResponse>('/api/BankStatements/list', req)
}

export function fetchBankStatementFilterValues() {
  return apiClient.get<BankStatementFilterValues>('/api/BankStatements/getAgGridFilters')
}

export function downloadBankStatement(id: number): Promise<Blob> {
  return apiClient.getBlob(`/api/BankStatements/${id}/download`)
}

export function deleteBankStatement(id: number): Promise<void> {
  return apiClient.delete<void>(`/api/BankStatements/${id}`)
}

export function analyseBankStatement(file: File): Promise<BankStatementAnalysis[]> {
  const fd = new FormData()
  fd.append('file', file)
  return apiClient.postForm<BankStatementAnalysis[]>('/api/BankStatements/analysis', fd)
}

export function uploadMultipleBankStatements(payload: BankStatementUploadPayload[]): Promise<void> {
  return apiClient.post<void>('/api/BankStatements/multiple', payload)
}

export function fetchBankStatementParsers(): Promise<BankStatementParser[]> {
  return apiClient.get<BankStatementParser[]>('/api/BankStatementParsers/forTenant')
}

export function fetchManualUploadAccounts(): Promise<ManualUploadAccount[]> {
  return apiClient.get<ManualUploadAccount[]>('/api/Accounts/manual-upload-accounts')
}
